// useCellClipboard
//
// Replaces TWO separate, partially-duplicated implementations:
//   - PlanogramVisual's copySourceCell/copiedCellKeys state + the full
//     Ctrl+C/X/V/Esc keydown handler (the only place keyboard clipboard
//     existed)
//   - PlanogramTabular's copySourceCell/copiedCellKeys state (mouse-only;
//     it never had keyboard support)
//
// Both views now get identical mouse-copy AND keyboard-clipboard behavior
// for free by using this one hook. All side effects (onDuplicateCell,
// onClearCell) are injected via callbacks, so this hook has zero
// view-specific DOM/layout knowledge — that lives in useDragSelect.
//
// *** FIX (GH #577): keyboard shortcuts owned by this hook (Ctrl+C/X/V,
// Delete/Backspace, Escape) must not fire against the BACKGROUND grid
// while a modal dialog (e.g. AddEditCellSettingsDialog's "assign product
// to selected cells" modal) is open on top of it. ***
//
// This hook's keydown listener is bound at the window level, same as
// useCellArrowNavigation's — so it sees every keypress on the page,
// including ones the person clearly intends for whatever's focused
// inside an open modal (typing into the modal's own price/qty fields,
// pressing Escape to close a dropdown, etc). Without a guard, Escape
// while a modal's own dropdown is open could be intercepted here and
// treated as "cancel the background grid's copy-mode/selection" instead
// of whatever the modal itself wanted Escape to do; Delete/Backspace
// while editing a modal field with no native text-selection to act on
// could likewise fall through into "clear the background-selected
// cells." See useCellArrowNavigation's identical GH #577 fix
// (hasOpenModalDialog) for the full rationale — this hook needs the same
// invariant for the same reason: once a modal is open, the grid
// underneath it is background UI and this hook's shortcuts must stay
// out of its way entirely.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlanogramClipboard } from '../domain/PlanogramClipboard';
import { CellCoordinates, CellKey, cellKey, parseCellKey } from '../domain/types';

type CellSnapshot = Record<string, unknown>;

const isObjectSnapshot = (value: unknown): value is CellSnapshot =>
    typeof value === 'object' && value !== null;

const cloneProduct = (product: unknown): unknown =>
    isObjectSnapshot(product) ? { ...product } : product ?? null;

/**
 * *** NEW (GH #577): true while ANY modal dialog is open anywhere on the
 * page, regardless of what's inside it. ***
 *
 * Identical selector and rationale to useCellArrowNavigation's
 * hasOpenModalDialog — kept as a separate copy rather than a shared
 * import since these two hooks otherwise have no dependency on each
 * other, and a one-line DOM query isn't worth introducing one. If this
 * selector ever needs to change, update both copies together.
 */
const hasOpenModalDialog = (): boolean =>
    !!document.querySelector('.p-dialog-mask, [role="dialog"]');

/**
 * Keyboard clipboard paste must write the cell data captured at Ctrl+C/X time,
 * while preserving the target cell's own identity/initialization metadata.
 * This mirrors PlanogramGridOps.duplicateCell, but uses a stored snapshot
 * instead of reading from the live source coordinates.
 */
const buildKeyboardPasteSnapshot = (targetSnapshot: unknown, sourceSnapshot: unknown): unknown => {
    if (!isObjectSnapshot(sourceSnapshot) || !isObjectSnapshot(targetSnapshot)) {
        return sourceSnapshot;
    }

    return {
        ...targetSnapshot,
        type: sourceSnapshot.type,
        product: cloneProduct(sourceSnapshot.product),
        price: sourceSnapshot.price,
        minQuantity: sourceSnapshot.minQuantity,
        maxQuantity: sourceSnapshot.maxQuantity,
        categoryColor: sourceSnapshot.categoryColor,
        currency: sourceSnapshot.currency,
        category: sourceSnapshot.category,
        productName: sourceSnapshot.productName,
    };
};

export interface UseCellClipboardOptions {
    rows: number;
    columns: number;
    /** Currently-active single cell (for keyboard copy/paste anchor), or null. */
    activeCellKey: CellKey | null;
    setActiveCellKey?: (key: CellKey | null) => void;
    /** Selection-mode integration — if active with a non-empty selection, Ctrl+C/X/Delete act on the whole selection. */
    isSelectionMode?: boolean;
    selectedKeys?: Set<CellKey>;
    onCancelSelection?: () => void;
    onDuplicateCell: (sourceRowIndex: number, sourceColIndex: number, targetRowIndex: number, targetColIndex: number) => void;
    onClearCell: (rowIndex: number, colIndex: number) => void;
    /**
     * GH #555: when Delete/Backspace is pressed while MULTIPLE cells are
     * selected, this is called instead of onClearCell-per-cell, so the
     * caller can show a confirmation dialog first (matching the toolbar's
     * existing "Clear cells" button) rather than clearing immediately. Not
     * called for a single active/selected cell — that still clears right
     * away, same as the existing per-cell clear action. If omitted, the
     * hook falls back to clearing immediately regardless of selection
     * size (the pre-existing behavior), so this is safe to add without
     * breaking other callers of this hook.
     */
    onClearSelectionRequest?: (targets: CellCoordinates[]) => void;
    /**
     * Returns a deep-cloneable snapshot of whatever is currently at
     * (rowIndex, colIndex) — read right before onDuplicateCell overwrites
     * that cell during mouse copy-mode, so cancelCopyMode can hand it back
     * via onRestoreCell. Required for Cancel to actually revert copied
     * cells (GH #515); if omitted, copy-mode still works but Cancel will
     * only clear the copy-mode UI state, same as before this fix.
     */
    getCellSnapshot?: (rowIndex: number, colIndex: number) => unknown;
    /**
     * Writes a previously-captured snapshot back into (rowIndex, colIndex)
     * — called once per affected cell when the user cancels an active
     * mouse copy-mode session, restoring exactly what was there before the
     * session started.
     */
    onRestoreCell?: (rowIndex: number, colIndex: number, snapshot: unknown) => void;
    /** Enables the global keydown listener (Ctrl+C/X/V/Delete/Esc). PlanogramTabular doesn't currently wire this up; PlanogramVisual does. */
    enableKeyboardShortcuts?: boolean;
    /** Called with a short toast label whenever a keyboard copy/cut/paste happens, anchored at the given cell — caller renders the floating toast. */
    onToast?: (rowIndex: number, colIndex: number, label: string) => void;
    labels?: {
        cut?: string;
        copied?: string;
        copiedCount?: (count: number) => string;
        pasted?: string;
    };
}

export function useCellClipboard(options: UseCellClipboardOptions) {
    const [clipboard, setClipboard] = useState<PlanogramClipboard>(PlanogramClipboard.empty());
    const optionsRef = useRef(options);
    optionsRef.current = options;

    // Keyboard Ctrl+C/X used to store only source coordinates. That breaks
    // Ctrl+X because the source is cleared immediately, so Ctrl+V later reads
    // an empty live source cell and overwrites the target with empty data.
    // Store immutable source snapshots alongside the coordinate clipboard.
    const keyboardSnapshotsRef = useRef<ReadonlyMap<CellKey, unknown>>(new Map());

    // -----------------------------------------------------------------------
    // Mouse copy-mode
    // -----------------------------------------------------------------------

    // *** FIX (GH #515): Cancel must revert every cell that was overwritten
    // during this copy-mode session, not just clear the copy-mode UI state.
    // ***
    //
    // Before this fix, cancelling only ever reset copySource/copiedKeys —
    // there was no way to undo the onDuplicateCell calls that
    // copyToCell/copyToRange already fired as the user dragged/clicked
    // across target cells (those are immediate, optimistic writes into the
    // real planogram data, not staged anywhere). So cancelling removed the
    // checkmarks/counter from the UI, but the copied product data stayed
    // permanently in every target cell — exactly the reported symptom.
    //
    // Fix: replay restoreSnapshots (captured per-cell, once each, the
    // first time it was stamped this session — see markCopied/
    // markCopiedMany) back through onRestoreCell before clearing the
    // clipboard state, so every affected cell returns to exactly what it
    // held before the session started. Shared by all three places that can
    // end an active copy-mode session this way: the toolbar Cancel button
    // (startCopyMode's (-1,-1) sentinel), cancelCopyMode() itself, and the
    // Escape-key shortcut.
    const revertAndCancel = useCallback((prev: PlanogramClipboard): PlanogramClipboard => {
        if (prev.restoreSnapshots.size > 0) {
            prev.restoreSnapshots.forEach((snapshot, key) => {
                const { rowIndex, colIndex } = parseCellKey(key);
                optionsRef.current.onRestoreCell?.(rowIndex, colIndex, snapshot);
            });
        }
        return prev.cancelCopyMode();
    }, []);

    const startCopyMode = useCallback((rowIndex: number, colIndex: number) => {
        if (rowIndex === -1 && colIndex === -1) {
            // The toolbar's "cancel copy mode" affordance reuses
            // startCopyMode's (-1, -1) sentinel — same revert behavior as
            // cancelCopyMode below.
            setClipboard(revertAndCancel);
            return;
        }
        setClipboard(prev => prev.startCopyMode(rowIndex, colIndex));
    }, [revertAndCancel]);

    const cancelCopyMode = useCallback(() => {
        setClipboard(revertAndCancel);
    }, [revertAndCancel]);

    // *** FIX (GH #544): Save and Cancel are not the same operation, and
    // must not share a code path that reverts. ***
    //
    // PlanogramCell's copy-mode toolbar renders two different buttons on
    // the source cell: a red Cancel (X) that calls onCancelCopyMode
    // directly, and a Save (floppy disk) that — until this fix — called
    // onDuplicateCell(-1, -1), which both PlanogramVisual and
    // PlanogramTabular wire straight to this hook's cancelCopyMode (via
    // startCopyMode's (-1,-1) sentinel above). That was harmless back when
    // cancelCopyMode was purely a UI-state reset with no revert behavior —
    // but once GH #515 gave Cancel its intended "restore every copied
    // cell back to what it held before" behavior, the mislabeled Save
    // button started silently reverting every copy it had just made, the
    // moment it was clicked. Exactly the reported "Save doesn't apply the
    // copied data" symptom — the copies WERE applied, then immediately
    // undone by the very button meant to confirm them.
    //
    // confirmCopyMode clears the same copy-mode state as cancelCopyMode
    // (copySource/copiedKeys/restoreSnapshots all reset) but — critically —
    // never replays restoreSnapshots through onRestoreCell. The data
    // that's already been written via onDuplicateCell during the session
    // simply stays as it is; this only stops tracking it as "part of an
    // active copy session."
    const confirmCopyMode = useCallback(() => {
        setClipboard(prev => prev.cancelCopyMode());
    }, []);

    const copyToCell = useCallback((targetRowIndex: number, targetColIndex: number) => {
        setClipboard(prev => {
            if (!prev.copySource) return prev;
            if (prev.copySource.rowIndex === targetRowIndex && prev.copySource.colIndex === targetColIndex) return prev;

            // *** FIX: clicking an already-copied target's checkbox a
            // second time must UN-check it, not just re-copy the same
            // data into it again. ***
            //
            // Before this fix, copyToCell only ever had one behavior —
            // copy the source into the target and mark it — regardless of
            // whether the target was already copied. There was no way to
            // remove a single cell from the copied set short of cancelling
            // the entire copy-mode session (which clears every target, not
            // just one). That's exactly the reported "I can check but not
            // uncheck" symptom: the checkbox stayed visually checked no
            // matter how many times you clicked it, because every click
            // took the same "copy here" branch.
            //
            // Now: if the target is already copied, this click instead
            // restores whatever was captured for it (the same per-cell
            // snapshot GH #515 already maintains for Cancel) and removes
            // it from the copied set — a real toggle, symmetric with how
            // the checkbox itself reads `checked`.
            if (prev.isCopied(targetRowIndex, targetColIndex)) {
                const snapshot = prev.getRestoreSnapshot(targetRowIndex, targetColIndex);
                optionsRef.current.onRestoreCell?.(targetRowIndex, targetColIndex, snapshot);
                return prev.unmarkCopied(targetRowIndex, targetColIndex);
            }

            // Snapshot must be read BEFORE onDuplicateCell runs — it's the
            // target cell's current (pre-overwrite) data, which is exactly
            // what Cancel needs to restore later.
            const previousCellData = optionsRef.current.getCellSnapshot?.(targetRowIndex, targetColIndex);
            optionsRef.current.onDuplicateCell(prev.copySource.rowIndex, prev.copySource.colIndex, targetRowIndex, targetColIndex);
            return prev.markCopied(targetRowIndex, targetColIndex, previousCellData);
        });
    }, []);

    const copyToRange = useCallback((keys: CellKey[]) => {
        setClipboard(prev => {
            if (!prev.copySource) return prev;

            // Snapshots MUST be captured before any onDuplicateCell call
            // below runs — once a target cell is overwritten, its
            // "current" data is the just-copied value, not the original
            // Cancel needs to restore. Only cells not already snapshotted
            // earlier in this same session need a fresh read (matches
            // markCopied's "first stamp wins" rule for a single cell).
            const snapshots = new Map<CellKey, unknown>();
            keys.forEach(key => {
                const { rowIndex, colIndex } = parseCellKey(key);
                if (rowIndex === prev.copySource!.rowIndex && colIndex === prev.copySource!.colIndex) return;
                if (prev.restoreSnapshots.has(key)) return;
                snapshots.set(key, optionsRef.current.getCellSnapshot?.(rowIndex, colIndex));
            });

            keys.forEach(key => {
                const { rowIndex, colIndex } = parseCellKey(key);
                if (rowIndex === prev.copySource!.rowIndex && colIndex === prev.copySource!.colIndex) return;
                optionsRef.current.onDuplicateCell(prev.copySource!.rowIndex, prev.copySource!.colIndex, rowIndex, colIndex);
            });

            return prev.markCopiedMany(keys, snapshots);
        });
    }, []);

    // If selection/copy mode is turned off elsewhere (e.g. the parent
    // component flips isSelectionMode), leave no stale copy-mode state OR
    // un-reverted copied cells behind — same GH #515 revert-then-clear as
    // an explicit Cancel, since this is also "the session ended without
    // the user confirming/saving."
    const resetIfInactive = useCallback((isActive: boolean) => {
        if (!isActive) setClipboard(prev => (prev.isCopyModeActive ? revertAndCancel(prev) : prev));
    }, [revertAndCancel]);

    // -----------------------------------------------------------------------
    // Keyboard clipboard (Ctrl+C / Ctrl+X / Ctrl+V / Delete / Esc)
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!options.enableKeyboardShortcuts) return;

        const isTextInputElement = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement =>
            target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

        const isContentEditableElement = (target: EventTarget | null): boolean =>
            target instanceof HTMLElement && target.isContentEditable;

        /** True if there's an actual (non-collapsed) text selection inside the field — i.e. native Ctrl+C/X or Delete/Backspace would act on selected text rather than the whole field. Inputs that don't expose selection offsets (e.g. type="number") are treated as "no selection", same as having a bare caret. */
        const hasTextSelection = (target: HTMLInputElement | HTMLTextAreaElement): boolean =>
            target.selectionStart !== null &&
            target.selectionEnd !== null &&
            target.selectionStart !== target.selectionEnd;

        /**
         * *** FIX (GH #555): Ctrl+C/X/V, Delete, and Backspace did nothing
         * at all in PlanogramTabular. ***
         *
         * Same root cause as useCellArrowNavigation's identical fix: this
         * hook's keydown listener bailed unconditionally whenever
         * event.target was an INPUT/TEXTAREA/contentEditable element. That
         * blanket guard is right for PlanogramVisual (no text inputs live
         * inside its grid cells) but wrong for PlanogramTabular, where
         * activeCellKey's focus normally sits on a real InputText for
         * whichever field is being edited (Product/EAN/Weight/Price/Min/
         * Max). The guard fired on every keypress there, so none of these
         * shortcuts ever reached the logic below — exactly the reported
         * "keyboard selection/shortcuts don't work in tabular view"
         * symptom.
         *
         * Deleting the guard outright would trade one bug for a worse one:
         * Ctrl+C while genuinely selecting text in a price field needs to
         * copy that text, not the whole cell; Backspace while correcting
         * a typo needs to delete a character, not wipe the cell's
         * product. So this yields to native behavior precisely when
         * there's something for native behavior to do, per key:
         *   - Ctrl+C / Ctrl+X: yield only if there's an actual (non-
         *     collapsed) text selection — nothing else for native copy/
         *     cut to act on otherwise, so cell-level copy/cut is safe.
         *   - Delete / Backspace: yield if there's a text selection (it
         *     would delete that text) OR a non-empty caret-adjacent
         *     character on the relevant side (Backspace deletes the
         *     character behind the caret; Delete removes the one ahead) —
         *     i.e. yield whenever native Delete/Backspace would actually
         *     remove something from the text. An empty field, or the
         *     caret already at the edge on the relevant side, has nothing
         *     for native behavior to do, so it falls through to the
         *     cell-level clear.
         *   - Ctrl+V: always yields when the target is a real text input —
         *     pasting text into a field being edited (e.g. a price) is
         *     ordinary, expected behavior, not something to intercept.
         *   - Escape: never part of this guard at all — it has no native
         *     text-editing meaning to protect either way.
         *   - contentEditable elements keep the original, more
         *     conservative behavior (always yield) since there's no
         *     reliable selection/caret API to reason about here.
         */
        const shouldYieldToNativeEditing = (target: EventTarget | null, key: string, ctrlOrMeta: boolean): boolean => {
            if (isContentEditableElement(target)) return true;
            if (!isTextInputElement(target)) return false;

            if (ctrlOrMeta && (key === 'c' || key === 'x')) {
                return hasTextSelection(target);
            }
            if (ctrlOrMeta && key === 'v') {
                return true;
            }
            if (key === 'Delete' || key === 'Backspace') {
                if (hasTextSelection(target)) return true;
                const { selectionStart, value } = target;
                if (selectionStart === null) return true; // no caret info — be conservative
                return key === 'Backspace' ? selectionStart > 0 : selectionStart < value.length;
            }
            return false;
        };

        const getSourceCells = (): CellCoordinates[] => {
            const opts = optionsRef.current;
            if (opts.isSelectionMode && opts.selectedKeys && opts.selectedKeys.size > 0) {
                return Array.from(opts.selectedKeys).map(parseCellKey);
            }
            if (opts.activeCellKey) {
                return [parseCellKey(opts.activeCellKey)];
            }
            return [];
        };

        const pasteKeyboardClipboardCell = (source: CellCoordinates, target: CellCoordinates) => {
            const opts = optionsRef.current;
            const sourceSnapshot = keyboardSnapshotsRef.current.get(cellKey(source.rowIndex, source.colIndex));

            if (sourceSnapshot !== undefined && opts.onRestoreCell && opts.getCellSnapshot) {
                const targetSnapshot = opts.getCellSnapshot(target.rowIndex, target.colIndex);
                if (targetSnapshot !== null && targetSnapshot !== undefined) {
                    opts.onRestoreCell(
                        target.rowIndex,
                        target.colIndex,
                        buildKeyboardPasteSnapshot(targetSnapshot, sourceSnapshot),
                    );
                    return;
                }
            }

            // Backward-compatible fallback for any caller that has not wired
            // snapshot restore support.
            opts.onDuplicateCell(source.rowIndex, source.colIndex, target.rowIndex, target.colIndex);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            const opts = optionsRef.current;

            // *** FIX (GH #577): once a modal dialog is open anywhere on
            // the page, NONE of this hook's shortcuts may act on the
            // background grid — see hasOpenModalDialog's own comment
            // above, and useCellArrowNavigation's identical GH #577 fix,
            // for the full rationale. Checked first, before even Escape
            // (which is otherwise deliberately NOT gated by
            // shouldYieldToNativeEditing below), since a modal's own
            // Escape handling — e.g. closing an open dropdown, or the
            // dialog itself — must always take priority over this hook's
            // "cancel the background copy-mode/selection" meaning for the
            // same key.
            if (hasOpenModalDialog()) return;

            if (event.key === 'Escape') {
                // Escape is just another way to cancel an active copy-mode
                // session — same GH #515 revert-then-clear as the toolbar
                // Cancel button and cancelCopyMode(). Deliberately NOT
                // gated by shouldYieldToNativeEditing — Escape has no
                // native text-editing behavior to protect, in an input or
                // anywhere else.
                setClipboard(prev => (prev.isCopyModeActive ? revertAndCancel(prev) : prev));
                if (opts.isSelectionMode) {
                    opts.onCancelSelection?.();
                } else {
                    opts.setActiveCellKey?.(null);
                }
                return;
            }

            if (event.key === 'Delete' || event.key === 'Backspace') {
                if (shouldYieldToNativeEditing(event.target, event.key, false)) return;

                const targets = getSourceCells();
                if (targets.length === 0) return;
                event.preventDefault();

                // *** GH #555 requirement 4: clearing a SINGLE selected
                // cell via Delete/Backspace still happens immediately
                // (identical to the existing per-cell clear button/
                // context-menu action — no extra confirmation for a
                // single cell, exactly as specified). Clearing MULTIPLE
                // selected cells must go through a confirmation dialog
                // first, matching the toolbar's existing "Clear cells"
                // button behavior (PlanogramDetails' showClearSelectedDialog)
                // rather than wiping every selected cell's product the
                // instant the key is pressed.
                //
                // onClearSelectionRequest is optional and additive: a
                // caller that doesn't pass it (or passes nothing) keeps the
                // old "clear immediately, no matter how many cells"
                // behavior for back-compat with any other usage of this
                // hook; PlanogramVisual/PlanogramTabular now both pass it
                // through to PlanogramDetails' existing dialog trigger.
                if (targets.length > 1 && opts.onClearSelectionRequest) {
                    opts.onClearSelectionRequest(targets);
                    return;
                }

                targets.forEach(({ rowIndex, colIndex }) => opts.onClearCell(rowIndex, colIndex));
                return;
            }

            const isModifierPressed = event.ctrlKey || event.metaKey;
            if (!isModifierPressed) return;
            const key = event.key.toLowerCase();
            if (shouldYieldToNativeEditing(event.target, key, true)) return;

            if (key === 'c' || key === 'x') {
                const sourceCells = getSourceCells();
                if (sourceCells.length === 0) return;
                event.preventDefault();

                keyboardSnapshotsRef.current = new Map(
                    sourceCells.map(({ rowIndex, colIndex }) => [
                        cellKey(rowIndex, colIndex),
                        opts.getCellSnapshot?.(rowIndex, colIndex),
                    ]),
                );

                setClipboard(prev => prev.copyToKeyboardClipboard(sourceCells, key === 'x'));

                const anchor = sourceCells[0];
                const label =
                    key === 'x'
                        ? opts.labels?.cut ?? 'Cut'
                        : sourceCells.length > 1
                        ? opts.labels?.copiedCount?.(sourceCells.length) ?? `Copied ${sourceCells.length} cells`
                        : opts.labels?.copied ?? 'Copied';
                opts.onToast?.(anchor.rowIndex, anchor.colIndex, label);

                if (key === 'x') {
                    sourceCells.forEach(({ rowIndex, colIndex }) => opts.onClearCell(rowIndex, colIndex));
                }
                return;
            }

            if (key === 'v') {
                let pastedCount = 0;
                let pasteAnchor: CellCoordinates | null = null;
                let consumedCutClipboard = false;

                setClipboard(prev => {
                    if (!prev.keyboardClipboard) return prev;

                    // *** FIX (GH #555): a real multi-cell selection must
                    // win over activeCellKey, not the other way around. ***
                    //
                    // Before this fix, `if (opts.activeCellKey) { ... }
                    // else if (opts.isSelectionMode ...)` checked
                    // activeCellKey FIRST — but activeCellKey is set
                    // essentially all the time (it's "whichever cell last
                    // had focus", selection or not), so the isSelectionMode
                    // branch was effectively dead code. Every paste
                    // degraded to a single-point pasteAnchor, and
                    // computePaste's offset-block math only ever produces
                    // one target pair for a single copied cell — hence
                    // "only the first/active cell gets filled" even with
                    // several cells selected. getSourceCells() above
                    // already gets this priority right for Ctrl+C/X/
                    // Delete; this makes paste consistent with it.
                    //
                    // With that priority fixed, a real multi-cell selection
                    // goes through computePasteIntoSelection — the correct
                    // model for "copy one cell, fill every selected cell"
                    // (see that method's own comment for why this is NOT
                    // just a generalization of the single-anchor block-paste
                    // math below). Single-cell selections (or no selection
                    // at all) fall through to the existing single-anchor
                    // computePaste, unchanged.
                    const hasRealSelection =
                        opts.isSelectionMode && opts.selectedKeys && opts.selectedKeys.size > 1;

                    if (hasRealSelection) {
                        const selectedKeyList = Array.from(opts.selectedKeys as Set<CellKey>);
                        const selectedCellCoords = selectedKeyList.map(parseCellKey);
                        const selectionTopLeft: CellCoordinates = {
                            rowIndex: Math.min(...selectedCellCoords.map(c => c.rowIndex)),
                            colIndex: Math.min(...selectedCellCoords.map(c => c.colIndex)),
                        };
                        const selectionPairs = prev.computePasteIntoSelection(selectedKeyList);

                        if (selectionPairs) {
                            selectionPairs.forEach(({ source, target }) => {
                                pasteKeyboardClipboardCell(source, target);
                            });
                            pastedCount = selectionPairs.length;
                            pasteAnchor = selectionTopLeft;
                            consumedCutClipboard = prev.keyboardClipboard.isCut;

                            return prev.consumeIfCut();
                        }
                        // computePasteIntoSelection returned null (e.g. a
                        // multi-cell block was copied, not a single cell) —
                        // fall through to the single-anchor path below,
                        // anchored at the selection's top-left, same as
                        // pre-fix behavior for that case.
                        pasteAnchor = selectionTopLeft;
                    } else if (opts.activeCellKey) {
                        pasteAnchor = parseCellKey(opts.activeCellKey);
                    } else if (opts.selectedKeys && opts.selectedKeys.size > 0) {
                        const cells = Array.from(opts.selectedKeys).map(parseCellKey);
                        pasteAnchor = {
                            rowIndex: Math.min(...cells.map(c => c.rowIndex)),
                            colIndex: Math.min(...cells.map(c => c.colIndex)),
                        };
                    }
                    if (!pasteAnchor) return prev;

                    const pairs = prev.computePaste(pasteAnchor, opts.rows, opts.columns);
                    pairs.forEach(({ source, target }) => {
                        pasteKeyboardClipboardCell(source, target);
                    });
                    pastedCount = pairs.length;
                    consumedCutClipboard = prev.keyboardClipboard.isCut;

                    return prev.consumeIfCut();
                });

                if (consumedCutClipboard) {
                    keyboardSnapshotsRef.current = new Map();
                }

                if (pastedCount > 0 && pasteAnchor) {
                    event.preventDefault();
                    opts.onToast?.(pasteAnchor.rowIndex, pasteAnchor.colIndex, opts.labels?.pasted ?? 'Pasted');
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // Stable: reads everything through optionsRef, so this effect only
        // needs to (re)bind once per enableKeyboardShortcuts toggle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.enableKeyboardShortcuts]);

    // *** FIX: same class of bug as useDragSelect's "Maximum update depth
    // exceeded" (see that file's comment for the full mechanism) ***
    //
    // This used to return a fresh object literal every render, with
    // isCopySourceCell/isCopied/areAllCopied re-bound via .bind(clipboard)
    // on every call — i.e. even the "stable" methods got new function
    // identities each render. Any consumer that depends on the whole
    // returned object (directly, or transitively through a dependency
    // array) would see a "changed" value every render regardless of
    // whether anything meaningful changed, which is the exact mechanism
    // that caused useDragSelect's infinite loop in PlanogramTabular.
    //
    // Memoizing on the clipboard domain instance is sufficient: it's the
    // only thing here that actually varies, and PlanogramClipboard's own
    // methods already return `this` unchanged when nothing should change
    // (see e.g. resetIfInactive above, and markCopied's no-op-on-source-cell
    // case in the domain class) — so `clipboard` itself only gets a new
    // identity when something real changed.
    return useMemo(() => ({
        clipboard,
        copySource: clipboard.copySource,
        isCopyModeActive: clipboard.isCopyModeActive,
        isCopySourceCell: clipboard.isCopySourceCell.bind(clipboard),
        isCopied: clipboard.isCopied.bind(clipboard),
        areAllCopied: clipboard.areAllCopied.bind(clipboard),
        copiedCount: clipboard.copiedKeys.size,
        startCopyMode,
        cancelCopyMode,
        confirmCopyMode,
        copyToCell,
        copyToRange,
        resetIfInactive,
    }), [clipboard, startCopyMode, cancelCopyMode, confirmCopyMode, copyToCell, copyToRange, resetIfInactive]);
}

export type UseCellClipboardReturn = ReturnType<typeof useCellClipboard>;